import { useCan } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { Alert, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";

import { api } from "../api";
import { CANCELLATION_REASONS, DATE_TIME_FORMAT, LESSON_STATUS, formatKurus } from "../labels";
import type { LessonItem } from "../types";
import { ConnectionCell } from "../components/connection";

const VIEW_OPTIONS = [
  { value: "past", label: "Geçmiş" },
  { value: "upcoming", label: "Yaklaşan" },
  { value: "all", label: "Tümü" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Tüm durumlar" },
  { value: "SCHEDULED", label: "Planlandı" },
  { value: "COMPLETED", label: "Tamamlandı" },
  { value: "CANCELLED", label: "İptal edildi" },
];

const CONNECTION_OPTIONS = [
  { value: "", label: "Tüm bağlantılar" },
  { value: "problem", label: "Bağlantı sorunu olan" },
];

const PAGE_SIZE = 20;

const CANCELLED_BY: Record<string, string> = {
  TUTOR: "Eğitmen",
  STUDENT: "Öğrenci",
  ADMIN: "Ekip",
};

/**
 * Lessons of everyone (/lessons), a tutor or a student: past first, with status, cancellation,
 * earning, rating and how many recordings it has (permission `lessons.view`; watching them is on
 * the lesson's page).
 */
export function LessonsTable({
  endpoint,
  showTutor,
  showStudent,
  initialConnection = "",
}: {
  /** e.g. `/api/office/lessons` or `/api/office/tutors/<id>/lessons`. */
  endpoint: string;
  showTutor: boolean;
  showStudent: boolean;
  /** "problem": only lessons with a connection problem (the dashboard links so). */
  initialConnection?: string;
}) {
  const [view, setView] = useState("past");
  const [status, setStatus] = useState("");
  const [connection, setConnection] = useState(initialConnection);
  const [page, setPage] = useState(1);
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const query = new URLSearchParams({ view, page: String(page), pageSize: String(PAGE_SIZE) });
  if (status) query.set("status", status);
  if (connection) query.set("connection", connection);
  const { data, isPending, error } = useQuery({
    queryKey: ["office", "lessons", endpoint, view, status, connection, page],
    queryFn: () => api<{ data: LessonItem[]; total: number }>(`${endpoint}?${query}`),
  });

  if (error) return <Alert type="error" showIcon message={error.message} />;

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={VIEW_OPTIONS}
          value={view}
          onChange={(value) => {
            setView(String(value));
            setPage(1);
          }}
        />
        <Select
          aria-label="Durum"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(value: string) => {
            setStatus(value);
            setPage(1);
          }}
          style={{ width: 160 }}
        />
        <Select
          aria-label="Bağlantı"
          options={CONNECTION_OPTIONS}
          value={connection}
          onChange={(value: string) => {
            setConnection(value);
            setPage(1);
          }}
          style={{ width: 200 }}
        />
      </Space>
      <Table<LessonItem>
        size="small"
        rowKey="id"
        loading={isPending}
        dataSource={data?.data}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "Ders yok" }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.total ?? 0,
          onChange: setPage,
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "Ders",
            dataIndex: "startsAt",
            render: (value: string, lesson) => (
              <Space direction="vertical" size={0}>
                <Link to={`/lessons/${lesson.id}`}>
                  <Typography.Text strong>{dayjs(value).format(DATE_TIME_FORMAT)}</Typography.Text>
                </Link>
                <Typography.Text type="secondary">
                  {lesson.subject} · {lesson.hours} saat
                </Typography.Text>
              </Space>
            ),
          },
          ...(showTutor
            ? [
                {
                  title: "Eğitmen",
                  dataIndex: "tutor",
                  render: (tutor: LessonItem["tutor"]) =>
                    canSeeTutors?.can ? (
                      <Link to={`/tutors/${tutor.id}`}>{tutor.name}</Link>
                    ) : (
                      tutor.name
                    ),
                },
              ]
            : []),
          ...(showStudent
            ? [
                {
                  title: "Öğrenci",
                  dataIndex: "student",
                  render: (student: string, lesson: LessonItem) =>
                    canSeeStudents?.can ? (
                      <Link to={`/students/${lesson.studentId}`}>{student}</Link>
                    ) : (
                      student
                    ),
                },
              ]
            : []),
          {
            title: "Durum",
            dataIndex: "status",
            render: (value: string, lesson) => (
              <Space direction="vertical" size={0}>
                <Tag color={LESSON_STATUS[value]?.color}>{LESSON_STATUS[value]?.label}</Tag>
                {lesson.status === "CANCELLED" ? (
                  <Typography.Text type="secondary">
                    {lesson.cancelledBy ? CANCELLED_BY[lesson.cancelledBy] : "—"}
                    {lesson.isLateCancellation ? " · geç iptal" : ""}
                    {lesson.cancellationReason
                      ? ` · ${CANCELLATION_REASONS[lesson.cancellationReason] ?? lesson.cancellationReason}`
                      : ""}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: "Kazanç",
            dataIndex: "earningKurus",
            render: (value: number | null) => (value === null ? "—" : formatKurus(value)),
          },
          {
            title: "Puan",
            dataIndex: "review",
            render: (review: LessonItem["review"]) => (review ? `${review.rating}/5` : "—"),
          },
          {
            title: "Bağlantı",
            dataIndex: "connection",
            render: (connection: LessonItem["connection"]) => (
              <ConnectionCell connection={connection} />
            ),
          },
          {
            title: "Ders kaydı",
            dataIndex: "recordingCount",
            render: (count: number) => (count > 0 ? <Tag color="blue">{count} kayıt</Tag> : "—"),
          },
        ]}
      />
    </>
  );
}
